import { isEqual } from 'lodash';
import { CONFLICTS_ALLOWED, CONFLICTS_ALLOWED_WILDCARD } from '../consts';
import type {
  ActiveConflicts,
  CommandConflictInfo,
} from '../context/KeyboardShortcutsContext';
import type { CommandKeys } from '../types/CommandKeys';
import { getCanonicalDisplayOrder } from '../utils/getKeyboardShortcutString';

export type ConflictCheckableCommand = {
  id: string;
  displayLabel?: string;
  keys?: ReadonlyArray<string>;
  alternativeKeys?: ReadonlyArray<string>;
  isCustomizable?: boolean;
};

/**
 * Detects keyboard shortcut conflicts across a set of commands.
 *
 * Two or more commands sharing the same key combination produce a conflict
 * unless every command involved is explicitly listed together in
 * {@link CONFLICTS_ALLOWED}. The returned record is keyed by command id and
 * lists every other command that shares its combination (minus allowed pairs).
 *
 * Pass commands with their *current* (possibly customized) keys. The help
 * dialog also re-runs this against a *simulated* command set (e.g. "what would
 * conflicts look like if these ids were reset to their defaults?").
 */
export function detectShortcutConflicts(
  commands: ReadonlyArray<ConflictCheckableCommand>
): ActiveConflicts {
  const conflicts: ActiveConflicts = {};

  const keyCombinationMap = new Map<string, CommandConflictInfo[]>();

  commands.forEach((command) => {
    // Wildcard-allowed commands (non-customizable tool shortcuts like
    // marquee/pan) don't participate in conflict detection at all — any
    // other command sharing their keys is allowed without flagging.
    if (CONFLICTS_ALLOWED_WILDCARD.includes(command.id)) return;
    const keys = command.keys || command.alternativeKeys;
    if (!keys || keys.length === 0) return;

    const canonicalKeys = getCanonicalDisplayOrder(keys as CommandKeys[]);
    const normalizedKeys = canonicalKeys.map((key) => key.toLowerCase());
    const keyCombination = normalizedKeys.join('+');

    const commandInfo: CommandConflictInfo = {
      id: command.id,
      displayLabel: command.displayLabel ?? '',
      category: '',
      isCustomizable: command.isCustomizable,
    };

    const existing = keyCombinationMap.get(keyCombination);
    if (existing) {
      existing.push(commandInfo);
    } else {
      keyCombinationMap.set(keyCombination, [commandInfo]);
    }
  });

  keyCombinationMap.forEach((commandsAtCombination) => {
    if (
      commandsAtCombination.length <= 1 ||
      !commandsAtCombination.some((cmd) => cmd.isCustomizable)
    ) {
      return;
    }

    const commandIds = commandsAtCombination.map((cmd) => cmd.id).sort();
    const isAllowedGroup = CONFLICTS_ALLOWED.some((allowedPair) => {
      const sortedAllowed = [...allowedPair].sort();
      return commandIds.every((id) => sortedAllowed.includes(id));
    });
    if (isAllowedGroup) return;

    commandsAtCombination.forEach((command) => {
      const others = commandsAtCombination.filter(
        (cmd) => cmd.id !== command.id
      );

      const disallowedOthers = others.filter((other) => {
        const pairIds = [command.id, other.id].sort();
        return !CONFLICTS_ALLOWED.some((allowedPair) => {
          const sortedAllowed = [...allowedPair].sort();
          return isEqual(pairIds, sortedAllowed);
        });
      });

      if (disallowedOthers.length > 0) {
        conflicts[command.id] = disallowedOthers;
      }
    });
  });

  return conflicts;
}
